import { memo, useMemo, Fragment, useCallback, useContext, useRef, useEffect } from 'react';
import { type Options as MarkdownOptions } from 'react-markdown';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { ChatQueryStatus, ChatResponseType, ReportFormat } from '@trinity/chat';
import { ChatMarkdown } from '@trinity/chat-components/components/Markdown/Markdown';
import { ScrollableTable } from '@trinity/chat-components/components/Markdown/ScrollableTable';
import type { SectionTag } from '@trinity/chat-components/components/presentation/SectionChip';
import { SECTION_CHIPS } from '@trinity/chat-components/components/presentation/SectionChip';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import {
  type CustomResponseComponent,
  type CustomResponseInput,
  getCustomChatResponseDefinition,
  TOP_LEVEL_CUSTOM_CHAT_RESPONSE_TYPES,
} from '@trinity/custom-pages-registry';
import { CustomPageContextContainer } from '../CustomPageContext';
import { ActiveChatSessionContext } from './context';
import { useDownloadQueryTable } from './hooks/useDownloadQueryTable';
import { BaseDownloadButton } from './Message.Download';
import { ChartResponse } from './Message.Response.Chart';
import { FindQuantAnalysesResponse } from './Message.Response.FindQuantAnalysis';
import { ChartQuantAnalysesResponse } from './Message.Response.QuantAnalysis';
import { FindQuotesResponse } from './Message.Response.Quotes';
import type { ResponseItem } from './Message.Response.utils';
import { parseResponse } from './Message.Response.utils';

type MessageResponseProps<T extends ChatResponseType = ChatResponseType> = {
  queryIdentifier: string;
  item: Chat.QueryResponse<T> | null;
  state: Chat.QueryState.Value<T>;
  status: ChatQueryStatus;
};

const HANDLED_AT_TOP_LEVEL = new Set<ChatResponseType>([
  ChatResponseType.MarketingMessage,
  ChatResponseType.HcpAvailability,
  ChatResponseType.HcpSearch,
  ChatResponseType.HcpSearchResults,
  ChatResponseType.HcpRecommendationsSearch,
  ChatResponseType.HcpSummary,
  ChatResponseType.HcpSelection,
  ChatResponseType.SchedulingAssistant,
  ChatResponseType.SmartRouting,
  ChatResponseType.FormWorkflow,
  ChatResponseType.SegmentationTagsNeeded,
  ChatResponseType.SegmentationQuestionsNeeded,
  ChatResponseType.SegmentationComplete,
  ChatResponseType.SegmentationCancelled,
  ChatResponseType.SegmentChatNeeded,
  ChatResponseType.SegmentChatCancelled,
  ChatResponseType.SegmentChatSelected,
  ChatResponseType.SegmentationUpload,
  ChatResponseType.SegmentationConfigure,
  ChatResponseType.SegmentationEDA,
  ChatResponseType.SegmentationStrategy,
  ChatResponseType.SegmentationRefinement,
  ChatResponseType.SegmentationFeatures,
  ChatResponseType.SegmentationResults,
  ChatResponseType.SegmentationExport,
  ...TOP_LEVEL_CUSTOM_CHAT_RESPONSE_TYPES,
]);

export const MessageResponse = ({ item, state, status, queryIdentifier }: MessageResponseProps) => {
  if (!item) {
    return null;
  }

  if (HANDLED_AT_TOP_LEVEL.has(item.type)) {
    return null;
  }

  const customResponseDefinition = getCustomChatResponseDefinition(item.type);
  if (customResponseDefinition && customResponseDefinition.renderLocation === 'message-response') {
    const Component = customResponseDefinition.component as CustomResponseComponent;
    return (
      <CustomPageContextContainer>
        <Component queryIdentifier={queryIdentifier} response={item as CustomResponseInput} />
      </CustomPageContextContainer>
    );
  }

  switch (item.type) {
    case ChatResponseType.Text:
      return <MessageResponseRawText value={item.value} />;
    case ChatResponseType.Markdown:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={false}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );
    case ChatResponseType.MarkdownHtml:
      return (
        <Markdown
          queryIdentifier={queryIdentifier}
          value={item.value}
          allowHtml={true}
          trustedDomains={['*']}
          showDownloadButton={status !== ChatQueryStatus.Streaming}
        />
      );
    case ChatResponseType.Chart:
      return <ChartResponse />;
    case ChatResponseType.BIChartQuantAnalyses:
      return <ChartQuantAnalysesResponse />;
    case ChatResponseType.BIFindQuotes:
      return <FindQuotesResponse data={item.data} />;
    case ChatResponseType.BIFindQuantAnalyses:
      return <FindQuantAnalysesResponse data={item.data} />;
    default: {
      return null;
    }
  }
};

type MarkdownProps = {
  queryIdentifier: string;
  value: string;
  allowHtml?: boolean;
  trustedDomains?: string[];
  showDownloadButton?: boolean;
};

type TaggedSection = {
  tag: SectionTag;
  content: string;
  traits: string[];
  preamble?: string;
};

const SECTION_TAG_PATTERN = /\*\*\[(Validated|Synthetic|Low confidence)\]\*\*/g;
const TRAITS_ANNOTATION_PATTERN = /<!--\s*traits:\s*\[[^\]]*\]\s*-->\s*/g;
const TRAITS_USED_PATTERN = /\*\*Traits used:\*\*\s*(.+)/;

function parseTaggedSections(value: string): TaggedSection[] | null {
  const tags: { tag: SectionTag; index: number }[] = [];
  let match: RegExpExecArray | null;
  const pattern = new RegExp(SECTION_TAG_PATTERN.source, 'g');

  while ((match = pattern.exec(value)) !== null) {
    tags.push({ tag: match[1] as SectionTag, index: match.index });
  }

  if (tags.length === 0) return null;

  const SECTION_ORDER: Record<SectionTag, number> = {
    Validated: 0,
    Synthetic: 1,
    'Low confidence': 2,
  };

  const preamble = tags[0].index > 0 ? value.slice(0, tags[0].index).trim() : '';

  const sections = tags.map((entry, i) => {
    const contentStart = entry.index + value.slice(entry.index).indexOf('\n') + 1;
    const contentEnd = i < tags.length - 1 ? tags[i + 1].index : value.length;
    let rawContent = value.slice(contentStart, contentEnd).trim();
    rawContent = rawContent.replace(TRAITS_ANNOTATION_PATTERN, '');

    let traits: string[] = [];
    const traitsMatch = rawContent.match(TRAITS_USED_PATTERN);
    if (traitsMatch) {
      traits = traitsMatch[1]
        .split(',')
        .map((t) => t.trim().replace(/_/g, ' '))
        .filter((t) => t.length > 0);
      rawContent = rawContent.replace(TRAITS_USED_PATTERN, '').trim();
    }

    return {
      tag: entry.tag,
      content: rawContent,
      traits,
      preamble: undefined,
    };
  });

  if (preamble && sections.length > 0) {
    sections[0].preamble = preamble;
  }

  // Only reorder when sections follow the standard assessment pattern
  // (all same-tag sections grouped). Engagement scripts interleave tags
  // intentionally (Synthetic → Validated → Synthetic) — preserve that order.
  // Skip reordering when preamble exists (survey Q labels depend on order).
  if (!preamble) {
    const isAlreadyGrouped = sections.every(
      (s, i) => i === 0 || SECTION_ORDER[s.tag] >= SECTION_ORDER[sections[i - 1].tag]
    );
    if (isAlreadyGrouped) {
      sections.sort((a, b) => SECTION_ORDER[a.tag] - SECTION_ORDER[b.tag]);
    }
  }

  return sections;
}

const TraitBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  borderRadius: 10,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: '18px',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(120, 65, 201, 0.15)' : '#F5F0FB',
  color: theme.palette.mode === 'dark' ? '#B89CE8' : '#7841C9',
  textTransform: 'capitalize',
}));

const TraitsRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginBottom: 6,
});

const Markdown = memo(
  ({ queryIdentifier, value, allowHtml, trustedDomains, showDownloadButton = true }: MarkdownProps) => {
    const session = useContext(ActiveChatSessionContext);
    const features = useContext(GroupFeaturesContext);

    const { download } = useDownloadQueryTable({
      chatInstanceId: session.id,
      queryIdentifier,
    });

    const handleDownload = useCallback(() => {
      download({
        title: `Generating Export`,
        name: `Table Data`,
        extension: ReportFormat.Excel,
      });
    }, [download]);

    const handleDownloadRef = useRef(handleDownload);
    useEffect(() => {
      handleDownloadRef.current = handleDownload;
    }, [handleDownload]);

    const sections = useMemo(() => parseTaggedSections(value), [value]);

    const customMarkdownComponents = useMemo(
      (): MarkdownOptions['components'] => ({
        table: (props) => {
          return (
            <>
              <ScrollableTable>{props.children}</ScrollableTable>
              {features.exportChatData && showDownloadButton && (
                <DownloadButtonWrapper>
                  <BaseDownloadButton label="Download List" onClick={() => handleDownloadRef.current()} />
                </DownloadButtonWrapper>
              )}
            </>
          );
        },
      }),
      [features.exportChatData, showDownloadButton]
    );

    if (sections) {
      return (
        <>
          {sections.map((section, i) => {
            const Chip = SECTION_CHIPS[section.tag];
            return (
              <Fragment key={i}>
                {section.preamble && (
                  <ChatMarkdown
                    components={customMarkdownComponents}
                    value={section.preamble}
                    allowHtml={allowHtml}
                    trustedDomains={trustedDomains}
                  />
                )}
                <Chip />
                {section.traits.length > 0 && (
                  <TraitsRow>
                    {section.traits.map((trait, j) => (
                      <TraitBadge key={j}>{trait}</TraitBadge>
                    ))}
                  </TraitsRow>
                )}
                <ChatMarkdown
                  components={customMarkdownComponents}
                  value={section.content}
                  allowHtml={allowHtml}
                  trustedDomains={trustedDomains}
                />
              </Fragment>
            );
          })}
        </>
      );
    }

    return (
      <ChatMarkdown
        components={customMarkdownComponents}
        value={value}
        allowHtml={allowHtml}
        trustedDomains={trustedDomains}
      />
    );
  }
);

type MessageResponseRawTextProps = {
  value: string;
};

export const MessageResponseRawText = memo(({ value }: MessageResponseRawTextProps) => {
  const items = useMemo(() => {
    return parseResponse(value);
  }, [value]);

  const renderItem = useCallback((item: ResponseItem, i: number) => {
    switch (item.type) {
      case 'bullets': {
        return (
          <StyledUl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledUl>
        );
      }
      case 'numbered': {
        return (
          <StyledOl key={i}>
            {item.value.map((value, j) => (
              <StyledLi key={j}>{value}</StyledLi>
            ))}
          </StyledOl>
        );
      }
      case 'text': {
        if (item.value === '') return <br key={i} />;
        return <div key={i}>{item.value}</div>;
      }
      default:
        return null;
    }
  }, []);

  return <Fragment>{items.map(renderItem)}</Fragment>;
});

const StyledLi = styled('li')({
  marginBottom: 4,

  '&:last-child': {
    marginBottom: 0,
  },
});

const StyledOl = styled('ol')({
  paddingLeft: 20,

  [`& > li`]: {
    paddingLeft: 5,
    marginBottom: 8,
    '&::marker': {
      fontFamily: 'var(--font-semibold)',
    },
  },
});

const StyledUl = styled('ul')({
  paddingInlineStart: 20,
});

const DownloadButtonWrapper = styled.div({
  marginTop: 8,
  marginBottom: 16,
});
